import { type PropsWithChildren } from "react";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { type TileDescriptor } from "@features/metrics-explorer/types";
import { jsonLogicToFriendlyText } from "@features/metrics-explorer/utils";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { formatDateRange } from "@features/sales-v3/utils/format-date-range";
import { NBAccountingMode } from "@north-beam/nb-common";

interface Props {
  tile: TileDescriptor;
  showTooltip: boolean;
  onOpen(): void;
  onClose(): void;
}

export const OverridesTooltip = ({
  tile,
  children,
  showTooltip,
  onOpen,
  onClose,
}: PropsWithChildren<Props>) => {
  const { user } = useUser();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const {
    filters: { timeGranularity, dateRangeInterval },
  } = useMetricsExplorerContext();

  const compareDateRangeInterval = tile.compareDateRange
    ? deriveComparisonDateRange(
        dateRangeInterval,
        tile.compareDateRange,
        user.timezone,
        timeGranularity,
      )
    : null;

  const compareDateRangeDisplayText = compareDateRangeInterval
    ? formatDateRange(compareDateRangeInterval)
    : "";

  return (
    <GenericTooltip
      onOpen={onOpen}
      onClose={onClose}
      noInfoCircle
      showTooltip={showTooltip}
      content={
        <div className="max-w-[600px]">
          <div className="font-bold text-sm mb-4">Overrides</div>
          <div className="space-y-4">
            {tile.attributionModel && (
              <div className="space-y-1">
                <div className="text-xxs text-muted">Attribution Model:</div>
                <div className="font-bold">
                  {user.attributionMethodEnum.getLabel(tile.attributionModel)}
                </div>
              </div>
            )}
            {tile.accountingMode && (
              <div className="space-y-1">
                <div className="text-xxs text-muted">Accounting Mode:</div>
                <div className="font-bold">
                  {NBAccountingMode.toReactSelect(tile.accountingMode).label}
                </div>
              </div>
            )}
            {tile.attributionWindow && (
              <div className="space-y-1">
                <div className="text-xxs text-muted">Attribution Window:</div>
                <div className="font-bold">{tile.attributionWindow} day</div>
              </div>
            )}
            {tile.compareDateRange && (
              <div className="space-y-1">
                <div className="text-xxs text-muted">Time comparison:</div>
                <div className="font-bold">{compareDateRangeDisplayText}</div>
              </div>
            )}
            {tile.filterQuery && (
              <div className="space-y-1">
                <div className="text-xxs text-muted">Search:</div>
                <div className="font-bold truncate">
                  {tile.filterQuery.query}
                </div>
              </div>
            )}
            {tile.advancedSearch && (
              <div className="space-y-1">
                <div className="text-xxs text-muted">Advanced Search:</div>
                <div className="font-bold">
                  {jsonLogicToFriendlyText(
                    tile.advancedSearch,
                    breakdownConfigs.map((e) => e.key),
                  )}
                </div>
              </div>
            )}
            {tile.filters.length > 0 && (
              <div className="space-y-1">
                <div className="text-xxs text-muted">Filters:</div>
                {tile.filters.map((e) => (
                  <div className="font-bold" key={e.value}>
                    {e.breakdown}: {e.value}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      }
    >
      {children}
    </GenericTooltip>
  );
};
